import React, { useEffect } from "react"
import { ProfileModel } from "../../reduxToolkit/membershipSlice"
import { SignatureCanvas } from "../signatureCanvas"
import { Tooltip } from "../tooltip"
import trimCanvas from "trim-canvas"
import toast from "react-hot-toast"
import { keysPresent, present } from "../../../lib/util/present"
import { NotNull } from "../../../types/util"

import './profileForm.scss'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface ProfileFormProps {
  initial?: Partial<ProfileModel> | null

  hideSignature?: boolean

  /**
   * Hides the phone and mailing address fields. Used during onboarding, where we
   * only need enough to identify the member — everything else is prefilled from
   * (and backfilled into) the forms that actually need it.
   */
  hidePhoneAndAddress?: boolean

  /**
   * The user's sign-in email from auth.users — displayed read-only above
   * the alternate-emails list so they understand it's already covered.
   * Empty string disables the alternate-emails section entirely.
   */
  signInEmail?: string

  /**
   * Rendered just above the submit button. Sections passed in here can carry
   * their own required elements; they gate submission via `canSubmit`.
   */
  children?: React.ReactNode

  /**
   * When false, the submit button is disabled. Lets a `children` section keep the
   * form from being submitted until its own required elements are satisfied.
   */
  canSubmit?: boolean

  /**
   * On submit, the only thing we can say about the profile is that it has the required fields.
   */
  onSubmit: (profile: FormFilledProfile) => Promise<void> | void
}

/**
 * The list of fields marked as "required" in the ProfileForm.
 *
 * Kept in sync with `isFilledProfile` — a profile the form calls complete has to
 * be one the app will actually load into the membership slice.
 */
const RequiredFields = [
  'full_name',
  'date_of_birth'
] as const
export type ProfileFormRequiredFields = typeof RequiredFields[number]

export type FormFilledProfile = NotNull<Partial<ProfileModel>, ProfileFormRequiredFields>

export function ProfileForm({
  initial,
  hideSignature,
  hidePhoneAndAddress,
  signInEmail,
  children,
  canSubmit = true,
  onSubmit: _onSubmit
}: ProfileFormProps) {
  const [row, setRow] = React.useState<Partial<ProfileModel>>(initial || {})
  const [submitting, setSubmitting] = React.useState(false)
  const [newEmail, setNewEmail] = React.useState('')

  // Defensive guard: persisted Redux state from before this feature deployed
  // will not have alternate_emails on the profile.
  const alternateEmails = row.alternate_emails ?? []

  const setAlternateEmails = (emails: string[]) => {
    setRow((current) => ({ ...current, alternate_emails: emails }))
  }

  const addAlternateEmail = () => {
    const candidate = newEmail.trim()
    if (!candidate) return
    if (!EMAIL_REGEX.test(candidate)) {
      toast.error('Please enter a valid email address')
      return
    }
    const candidateLower = candidate.toLowerCase()
    if (signInEmail && signInEmail.toLowerCase() === candidateLower) {
      toast.error('That is already your sign-in email')
      return
    }
    if (alternateEmails.some((e) => e.toLowerCase() === candidateLower)) {
      toast.error('That email is already in your list')
      return
    }
    setAlternateEmails([...alternateEmails, candidate])
    setNewEmail('')
  }

  const removeAlternateEmail = (email: string) => {
    setAlternateEmails(alternateEmails.filter((e) => e !== email))
  }

  return <form className="profile-form" onSubmit={onSubmit}>
      <div className="form-group">
        <label htmlFor="fullName" className="form-label">Full Name</label>
        <input type="text" className="form-control" id="fullName"
          required
          value={row.full_name|| ''}
          onChange={(e) => { setRow({...row, full_name: e.target.value }) }} />
      </div>
      <div className="form-group">
        <label htmlFor="dob" className="form-label">Date of Birth</label>
        <input name="datepic" placeholder="DateRange" type="date" id="dob"
          className="form-control"
          required
          value={row.date_of_birth || ''}
          onChange={(e) => { setRow({...row, date_of_birth: e.target.value }) }}></input>
      </div>

      {!hidePhoneAndAddress && <>
        <div className="form-group row">
          <div className='col-12 col-lg-9'>
            <label htmlFor="phoneNum" className="form-label">Phone Number</label>
            <input type="phone" className="form-control" id="phoneNum"
              value={row.phone|| ''}
              onChange={(e) => { setRow({...row, phone: e.target.value }) }} />
            </div>

          <div className='col-12 col-lg-3'>
            <label htmlFor="phoneType" className="form-label">Phone Type</label>
            <select className="form-select" aria-label="Default select example"
                value={row.phone_type || ''}
                onChange={(e) => { setRow({...row, phone_type: e.target.value }) }}>
              <option value="cell">Cell</option>
              <option value="home">Home</option>
              <option value="work">Work</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="memberNumber" className="form-label">Full Address</label>
          <textarea className="form-control" id="address"
            value={row.address|| ''}
            onChange={(e) => { setRow({...row, address: e.target.value }) }} />
          <small className="form-text text-muted">
            Used on reimbursement forms and when Alice contacts a provider for you.
            Use "enter" to separate lines.
          </small>
        </div>
      </>}

      {signInEmail && <div className="form-group">
        <label className="form-label">Alternate Email Addresses</label>
        <div className="text-muted small mb-2">
          Sign-in email: <strong>{signInEmail}</strong>
        </div>
        {alternateEmails.length > 0 && <ul className="list-group mb-2">
          {alternateEmails.map((email) => (
            <li key={email} className="list-group-item d-flex justify-content-between align-items-center">
              <span>{email}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-label={`Remove ${email}`}
                onClick={() => removeAlternateEmail(email)}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>}
        <div className="input-group">
          <input
            type="email"
            className="form-control"
            placeholder="Add another email (e.g. personal@example.com)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addAlternateEmail()
              }
            }}
          />
          <button type="button" className="btn btn-outline-primary" onClick={addAlternateEmail}>Add</button>
        </div>
        <small className="form-text text-muted">
          Used when contacting providers. Removing an email here does not change which email any
          specific provider has on file for you — edit that provider to update its email.
        </small>
      </div>}

      {!hideSignature && <hr />}

      {!hideSignature && <div className="col-12 signature-row">
        <label>
          Your E-Signature
          &nbsp;
          <Tooltip tooltip="We use this to sign CHM forms on your behalf."/>
        </label>
        <SignatureCanvas data={row.signature_data}
        onChange={(data) => { setRow({...row, signature_data: data }) }} />
      </div>}

      {children}

      <div className="col-12 mt-2">
          <button type='submit'
              className={`btn btn-primary`}
              disabled={!canSubmit || submitting}
          >
            {submitting ?
              <span className="spinner-border" role="status">
                <span className="visually-hidden">Submitting...</span>
              </span> :
              `Submit`}
          </button>
      </div>
    </form>

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!(e.target as HTMLFormElement).checkValidity()) { return }

    async function doSubmit() {
      const canSubmit = keysPresent(...RequiredFields)
      if (!canSubmit(row)) { throw new Error('Profile not complete!') }

      setSubmitting(true)
      try {
        // clone the row so we can postprocess it (react state is read only)
        const toSubmit = { ...row }
        if (present(toSubmit.signature_data)) {
          toSubmit.signature_data = await postProcessSignatureData(toSubmit.signature_data)
        }

        await _onSubmit(toSubmit)
      } catch (e) {
        console.error(e)
        toast.error('Something went wrong!  Please try again')
      } finally {
        setSubmitting(false)
      }
    }
    doSubmit()
  }
}

/**
 * Trims the signature data to the actual signature
 */
export function postProcessSignatureData(imageDataUrl: string): Promise<string> {
  // write the image to the canvas
  let canvas = document.createElement('canvas')
  let ctx = canvas.getContext('2d')!
  let img = new Image()
  img.src = imageDataUrl

  return new Promise<string>((resolve, reject) => {
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      
      // then trim it
      canvas = trimCanvas(canvas)
      
      resolve(canvas.toDataURL())
    }
    img.onerror = reject
  }).finally(() => {
    canvas.remove()
  })
}
